import React from 'react'
import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { AppRoutes } from '@core/AppRoutes'
import { generateShortenedUrl } from '@core/Areas/ShortenedUrls'
import { doesClientSupportShare } from '@core/Areas/ShareBet/helpers/doesClientSupportShare'
import type { FollowMeProfile } from '@core/Areas/FollowMe/types'
import { createImageBase64FromHtml } from '@core/Areas/ShareBet/ShareBetOutgoing/Components/ShareWithExternal/helpers/createImageFromHtml'
import { ShareProfileContent } from './Components/ShareProfileContent'

const enum LocalConstants {
  ShareTitleCopy = 'Check out this Profile on TABTouch!',
  ShareMessageCopy = "Want to follow my bets? I'm on Bet Loop by TABtouch. Check out my profile ",
}

export const useShareProfile = (profile: FollowMeProfile) => {
  const shareElRef = React.useRef<HTMLDivElement>(null)

  const [canShare] = React.useState(doesClientSupportShare)
  const [shouldRenderNativeShareEl, setShouldRenderNativeShareEl] = React.useState(false)
  const [isPreparingNativeShare, setIsPreparingNativeShare] = React.useState(false)
  const [isPreparingWebShare, setIsPreparingWebShare] = React.useState(false)

  // Handle Native Share Programmatically
  React.useEffect(() => {
    if (!shouldRenderNativeShareEl || !isPreparingNativeShare) return

    shareWithNative({ profileId: profile.id, previewEl: shareElRef.current }).finally(() => {
      setIsPreparingNativeShare(false)
    })
  }, [profile.id, shouldRenderNativeShareEl, isPreparingNativeShare])

  const handleShareProfileClick = () => {
    if (isReactNativeApp) {
      setShouldRenderNativeShareEl(true)
      setIsPreparingNativeShare(true)
      return
    } else {
      setIsPreparingWebShare(true)
      shareWithBrowser(profile.id)
        .finally(() => setIsPreparingWebShare(false))
        .catch(err => {
          const errorText = (err as Error).toString?.().toLowerCase()
          if (errorText && (errorText.includes('cancel') || errorText.includes('abort'))) return
          addToast({
            id: 'Share-Profile-Web-Error',
            timeoutMs: Infinity,
            isDismissable: true,
            title: 'Unable to Share Profile',
            icon: 'error',
            theme: 'dark',
          })
        })
    }
  }

  return {
    canShare,
    handleShareProfileClick,
    isLoadingShare: isPreparingWebShare || isPreparingNativeShare,
    /** ONLY renders on initiating native share (outside of viewport) */
    NativeShareProfileContent: () =>
      shouldRenderNativeShareEl ? <ShareProfileContent profile={profile} ref={shareElRef} /> : null,
  }
}

// =============
// Local Helpers
// =============

async function shareWithNative({
  profileId,
  previewEl,
}: {
  profileId: string
  previewEl: HTMLDivElement | null
}) {
  if (!previewEl) throw Error('Unable to create share')

  const [base64Image, shareUrl] = await Promise.all([
    await createImageBase64FromHtml(previewEl),
    await buildShareUrl(profileId),
  ])

  const shareData = {
    url: base64Image,
    title: LocalConstants.ShareTitleCopy,
    subject: LocalConstants.ShareTitleCopy,
    message: LocalConstants.ShareMessageCopy + shareUrl,
  }

  sendToNative('SHARE_SHEET_REQUEST', shareData)
}

async function shareWithBrowser(id: FollowMeProfile['id']) {
  const shareUrl = await buildShareUrl(id)

  const { title, message } = {
    title: LocalConstants.ShareTitleCopy,
    message: LocalConstants.ShareMessageCopy + shareUrl,
  }

  const navigatorShareData: ShareData = { title, text: message }

  if (navigator.canShare?.(navigatorShareData)) {
    return await navigator.share(navigatorShareData)
  }
  throw Error('No capability to share')
}

const buildShareUrl = async (profileId: string): Promise<string> => {
  const rawLink = `${window.location.origin}${AppRoutes.BetLoopProfile}/${profileId}`
  const shortenedUrl = await generateShortenedUrl(new URL(rawLink))
  return shortenedUrl.toString()
}
